import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUnreadCount } from '../../app/ShellControls';
import { Icon, type IconName } from '../../components/icons';
import { Alert, Button, Card, StatusBadge, buttonClasses } from '../../components/ui';
import { useToast } from '../../components/ui/toast';
import { postsApi, type AutomationSummary, type ScheduledPost } from '../../lib/api';
import { formatAmount } from '../../lib/format';
import { INVOICES_ANCHOR, invoiceDay, useInvoices } from '../billing/invoices';
import { isPlanBlock } from '../billing/plan';
import { CampaignStatusBadge } from '../campaigns/shared';
import { errorMessage as actionError } from '../contacts/shared';
import type { ListKind } from '../posts/postRows';
import { canRetry, failedOf, untimedByCampaign } from '../posts/schedule';
import { CardLink, RowsSkeleton, errorMessage } from './widgets';

/**
 * "À traiter" (Murmura M1, `P1-Dashboard.dc.html`, D-186): one row per thing
 * someone must act on, each with the action the app already has, in this
 * order: open invoices (one row each, no dismissal: Q22), failed posts and
 * stories (Réessayer with one failure, Voir: Q23), draft campaigns with posts
 * that have no time, draft automations, the first three, then one line to
 * Automatisations. The header counts everything to act on, folded drafts
 * included; the footer names what is clear. It replaces the dismissible
 * invoice notice; the "Facture" pill in the navigation stays.
 */

/**
 * Hichem, 2026-11-01, after the M1 visual check: the queue folds draft
 * automations after three; invoices, failures and campaigns never fold.
 */
export const DRAFT_AUTOMATIONS_SHOWN = 3;

type Tone = 'warning' | 'danger' | 'primary';

const TILE: Record<Tone, string> = {
  warning: 'bg-warning-50 text-warning-700',
  danger: 'bg-danger-50 text-danger-700',
  primary: 'bg-primary-50 text-primary-700',
};

interface TodoRow {
  key: string;
  tone: Tone;
  icon: IconName;
  title: ReactNode;
  badge?: ReactNode;
  body?: ReactNode;
  /** The body in red: an invoice past its due date. */
  urgent?: boolean;
  actions: ReactNode;
}

const ACTION = `touch-target ${buttonClasses('secondary', 'sm')}`;

/** Where "Voir" lands: the list on "Échecs & annulées" (Q23). */
const FAILED_LIST: Record<ListKind, string> = { post: '/posts?tab=failed', story: '/stories?tab=failed' };

function Row({ row }: { row: TodoRow }) {
  return (
    <li className="flex items-start gap-3 border-b border-border px-5 py-3.5 last:border-0">
      <span aria-hidden className={`flex size-[34px] shrink-0 items-center justify-center rounded-ds-md ${TILE[row.tone]}`}>
        <Icon name={row.icon} className="size-4" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
            <b className="min-w-0 break-words font-semibold text-ink">{row.title}</b>
            {row.badge}
          </p>
          {row.body ? <p className={`break-words text-ds-sm ${row.urgent ? 'text-danger-700' : 'text-ink-2'}`}>{row.body}</p> : null}
        </div>
        <div className="flex flex-wrap gap-2">{row.actions}</div>
      </div>
    </li>
  );
}

export function TodoQueue({
  automations,
  automationsLoading,
  className = '',
}: {
  automations: AutomationSummary[] | undefined;
  automationsLoading: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const invoices = useInvoices();
  const unread = useUnreadCount();
  // Shared with "Prochaine mise en ligne" and Publications; no polling here (PLAN.md §7).
  const posts = useQuery({ queryKey: ['posts'], queryFn: postsApi.list });

  // The list's retry, with its invalidations and its toast (PostsPage, A08-01).
  const retry = useMutation({
    mutationFn: async (post: ScheduledPost) => {
      await postsApi.retry(post.id);
    },
    onSuccess: async (_, post) => {
      queryClient.removeQueries({ queryKey: ['post', post.id] });
      await queryClient.invalidateQueries({ queryKey: ['posts'] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      await queryClient.invalidateQueries({ queryKey: ['campaign'] });
      toast({ title: t(`posts.kind.${post.type === 'story' ? 'story' : 'post'}.retried`), tone: 'success' });
    },
    // A plan refusal (402) is the shared toast's, with "Changer d'offre" (D-183).
    onError: (err) => {
      if (!isPlanBlock(err)) toast({ title: t('posts.actionFailed'), body: actionError(err), tone: 'error' });
    },
  });

  const rows: TodoRow[] = [];

  // An agent gets a 403 and no data: no invoice row (D-021).
  for (const invoice of invoices.data?.open ?? []) {
    const overdue = invoice.status === 'overdue';
    const date = invoiceDay(invoice.dueAt);
    rows.push({
      key: `invoice:${invoice.id}`,
      tone: overdue ? 'danger' : 'warning',
      icon: 'receipt',
      title: t('dashboard.todo.invoice.title', { number: invoice.number, amount: formatAmount(invoice.amountDue, invoice.currency) }),
      body: t(overdue ? 'dashboard.todo.invoice.overdue' : 'dashboard.todo.invoice.due', { date }),
      urgent: overdue,
      actions: (
        <Link to={`/settings/usage#${INVOICES_ANCHOR}`} className={`touch-target ${buttonClasses('primary', 'sm')}`}>
          {t('dashboard.todo.invoice.view')}
        </Link>
      ),
    });
  }

  const all = posts.data ?? [];
  const failed = failedOf(all);
  if (posts.isSuccess) {
    for (const kind of ['post', 'story'] as const) {
      const items = failed[kind];
      if (items.length === 0) continue;
      const single = items.length === 1 ? items[0]! : null;
      rows.push({
        key: `failed:${kind}`,
        tone: 'danger',
        icon: 'alert',
        title: t(`dashboard.todo.failed.${kind}`, { count: items.length }),
        // The platform's reason, as the list shows it; several failures: the list says each one's.
        body: single?.lastError ?? undefined,
        actions: (
          <>
            {single && canRetry(single) ? (
              <Button variant="secondary" size="sm" loading={retry.isPending} onClick={() => retry.mutate(single)}>
                {t('dashboard.todo.failed.retry')}
              </Button>
            ) : null}
            <Link to={FAILED_LIST[kind]} className={ACTION}>
              {t('dashboard.todo.failed.view')}
            </Link>
          </>
        ),
      });
    }
    for (const campaign of untimedByCampaign(all)) {
      rows.push({
        key: `campaign:${campaign.id}`,
        tone: 'warning',
        icon: 'clock',
        title: campaign.name,
        badge: <CampaignStatusBadge status="draft" />,
        body: t('dashboard.todo.campaign.untimed', { count: campaign.count }),
        actions: (
          <Link to={`/campaigns/${campaign.id}`} className={ACTION}>
            {t('dashboard.todo.campaign.setTimes')}
          </Link>
        ),
      });
    }
  }

  // Draft automations, in the list's order: the first few as rows, the rest as one line.
  const drafts = automations?.filter((a) => a.status === 'draft') ?? [];
  const shownDrafts = drafts.slice(0, DRAFT_AUTOMATIONS_SHOWN).map((automation): TodoRow => ({
    key: `automation:${automation.id}`,
    tone: 'primary',
    icon: 'workflow',
    title: automation.name,
    badge: <StatusBadge status="draft" />,
    body: t('dashboard.todo.automation.notLive'),
    actions: (
      <Link to={`/automations/${automation.id}`} className={ACTION}>
        {t('automationsList.finishSetup')}
      </Link>
    ),
  }));
  const hiddenDrafts = drafts.length - shownDrafts.length;
  // Everything to act on, folded drafts included: the pill and the empty state read this.
  const total = rows.length + drafts.length;

  const loading = posts.isLoading || automationsLoading;
  const noFailures = posts.isSuccess && failed.post.length === 0 && failed.story.length === 0;
  const clear = noFailures && unread === 0 ? 'both' : noFailures ? 'noFailures' : unread === 0 ? 'noUnread' : null;

  return (
    <Card
      flush
      title={t('dashboard.todo.title')}
      className={className}
      aside={
        !loading && total > 0 ? (
          <span className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-danger-600 px-[7px] text-ds-xs font-bold text-white">
            <span aria-hidden>{total}</span>
            <span className="sr-only">{t('dashboard.todo.count', { count: total })}</span>
          </span>
        ) : undefined
      }
      footer={!loading && clear ? t(`dashboard.todo.clear.${clear}`) : undefined}
    >
      {posts.isError ? (
        <div className="px-5 pt-4">
          <Alert>{errorMessage(posts.error, t)}</Alert>
        </div>
      ) : null}
      {loading ? (
        <RowsSkeleton />
      ) : total > 0 ? (
        <ul>
          {[...rows, ...shownDrafts].map((row) => (
            <Row key={row.key} row={row} />
          ))}
          {hiddenDrafts > 0 ? (
            <li className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-5 py-3">
              <span className="text-ds-sm text-ink-2">{t('dashboard.todo.automation.more', { count: hiddenDrafts })}</span>
              <CardLink to="/automations">{t('dashboard.todo.automation.viewAll')}</CardLink>
            </li>
          ) : null}
        </ul>
      ) : (
        <p className="px-5 py-5 text-ds-sm text-ink-3">{t('dashboard.todo.nothing')}</p>
      )}
    </Card>
  );
}
